import React, { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { mendyxRankingTranslations } from '../i18n/translations';

const CLASSEMENT_URL =
  'https://us-central1-mendyx-adafe.cloudfunctions.net/getClassement';

const ClassementMendyx = () => {
  const { language } = useLanguage();
  const t = createTranslator(language, mendyxRankingTranslations);
  const [players, setPlayers] = useState([]);
  const [champions, setChampions] = useState([]);
  const [viewMode, setViewMode] = useState('elo');

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFullLoading, setIsFullLoading] = useState(false);
  const [isChampionsLoading, setIsChampionsLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({
    key: 'elo',
    direction: 'descending',
  });

  const normalizeText = (value) =>
    value
      ?.toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim() || '';

  const cleanPlayers = (data) =>
    data
      .filter((player) => {
        if (!player.elo || !player.nom || !player.prenom) return false;

        // P400 maximum = on exclut les P500, donc Elo inférieur à 2000.
        return Number(player.elo) < 2000;
      })
      .sort((a, b) => b.elo - a.elo);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const topResponse = await fetch(`${CLASSEMENT_URL}?limit=300`);
        const topData = await topResponse.json();

        setPlayers(cleanPlayers(topData));
        setIsInitialLoading(false);
        setIsFullLoading(true);

        const fullResponse = await fetch(CLASSEMENT_URL);
        const fullData = await fullResponse.json();

        setPlayers(cleanPlayers(fullData));
      } catch (error) {
        console.error('Erreur de chargement du classement :', error);
      } finally {
        setIsInitialLoading(false);
        setIsFullLoading(false);
      }
    };

    fetchPlayers();
  }, []);

  useEffect(() => {
    const fetchChampions = async () => {
      try {
        setIsChampionsLoading(true);

        const snap = await getDocs(collection(db, 'winners'));
        const championsMap = {};

        snap.forEach((document) => {
          const data = document.data();

          if (!Array.isArray(data.players)) return;

          data.players.forEach((player) => {
            const nom = player.nom?.toString().trim() || '';
            const prenom = player.prenom?.toString().trim() || '';

            if (!nom && !prenom) return;

            const key = `${normalizeText(nom)}-${normalizeText(prenom)}`;

            if (!championsMap[key]) {
              championsMap[key] = {
                nom,
                prenom,
                victories: 0,
              };
            }

            championsMap[key].victories += 1;
          });
        });

        const championsList = Object.values(championsMap).sort((a, b) => {
          if (b.victories !== a.victories) {
            return b.victories - a.victories;
          }

          return `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`);
        });

        setChampions(championsList);
      } catch (error) {
        console.error('Erreur de chargement des champions :', error);
      } finally {
        setIsChampionsLoading(false);
      }
    };

    fetchChampions();
  }, []);

  const getCategorieFromElo = (elo) => {
    if (elo >= 2000) return 'P500';
    if (elo >= 1600) return 'P400';
    if (elo >= 1200) return 'P300';
    if (elo >= 900) return 'P200';
    if (elo >= 800) return 'P150';
    if (elo >= 700) return 'P100';
    if (elo >= 200) return 'P50';
    return t('notRanked');
  };

  const getCategoryStyle = (elo) => {
    if (elo >= 2000) return 'bg-purple-100 text-[#4c2f8e] border-purple-200';
    if (elo >= 1600) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (elo >= 1200) return 'bg-cyan-100 text-cyan-700 border-cyan-200';
    if (elo >= 900) return 'bg-green-100 text-green-700 border-green-200';
    if (elo >= 700) return 'bg-yellow-100 text-yellow-700 border-yellow-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const requestSort = (key) => {
    let direction = 'ascending';

    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }

    setSortConfig({ key, direction });
  };

  const sortedPlayers = useMemo(() => {
    const list = [...players];

    list.sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }

      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }

      return 0;
    });

    return list;
  }, [players, sortConfig]);

  const filteredPlayers = useMemo(() => {
    const term = normalizeText(searchTerm);

    if (!term) return sortedPlayers;

    return sortedPlayers.filter((player) => {
      const fullName = normalizeText(`${player.nom} ${player.prenom}`);
      const reverseName = normalizeText(`${player.prenom} ${player.nom}`);
      const nom = normalizeText(player.nom);
      const prenom = normalizeText(player.prenom);

      return (
        fullName.includes(term) ||
        reverseName.includes(term) ||
        nom.includes(term) ||
        prenom.includes(term)
      );
    });
  }, [searchTerm, sortedPlayers]);

  const filteredChampions = useMemo(() => {
    const term = normalizeText(searchTerm);

    if (!term) return champions;

    return champions.filter((player) => {
      const fullName = normalizeText(`${player.nom} ${player.prenom}`);
      const reverseName = normalizeText(`${player.prenom} ${player.nom}`);
      const nom = normalizeText(player.nom);
      const prenom = normalizeText(player.prenom);

      return (
        fullName.includes(term) ||
        reverseName.includes(term) ||
        nom.includes(term) ||
        prenom.includes(term)
      );
    });
  }, [searchTerm, champions]);

  const getRealRank = (player) =>
    sortedPlayers.indexOf(player) + 1;

  const getChampionRealRank = (player) =>
    champions.findIndex(
      (p) =>
        normalizeText(p.nom) === normalizeText(player.nom) &&
        normalizeText(p.prenom) === normalizeText(player.prenom)
    ) + 1;

  const handleDownloadClassementPDF = () => {
    const doc = new jsPDF();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(
      viewMode === 'elo' ? t('title') : t('pdfChampions'),
      10,
      20
    );

    if (viewMode === 'elo') {
      const tableData = filteredPlayers.map((player) => [
        getRealRank(player),
        `${player.nom} ${player.prenom}`,
        player.elo?.toFixed(0),
        getCategorieFromElo(player.elo),
      ]);

      doc.autoTable({
        head: [['#', t('player'), 'Elo', t('category')]],
        body: tableData,
        startY: 35,
        headStyles: {
          fillColor: [76, 47, 142],
          textColor: [255, 255, 255],
        },
      });

      doc.save('classement_mendyx_p400_max.pdf');
      return;
    }

    const championsData = filteredChampions.map((player) => [
      getChampionRealRank(player),
      `${player.nom} ${player.prenom}`,
      player.victories,
    ]);

    doc.autoTable({
      head: [['#', t('winningPlayer'), t('wins')]],
      body: championsData,
      startY: 35,
      headStyles: {
        fillColor: [76, 47, 142],
        textColor: [255, 255, 255],
      },
    });

    doc.save('classement_champions_mendyx.pdf');
  };

  return (
    <section className="min-h-screen bg-gradient-to-b from-white to-[#f7f4ff] text-black">
      <div className="w-full max-w-[1600px] mx-auto pt-16 md:pt-24 pb-8 md:pb-12 px-3 sm:px-6 lg:px-12">
        {/* HEADER */}
        <div className="text-center mb-3 md:mb-8">
          <p className="text-[9px] md:text-xs font-bold uppercase tracking-[0.22em] md:tracking-[0.35em] text-[#4c2f8e] mb-1 md:mb-3">
            {t('brand')}
          </p>

          <h1 className="text-2xl sm:text-4xl md:text-6xl font-black text-[#4c2f8e] leading-tight">
            {t('title')}
          </h1>

          {/* BOUTONS */}
          <div className="flex flex-row justify-center items-center gap-2 mt-4 md:mt-9 mb-2 md:mb-5">
            <button
              type="button"
              onClick={() => {
                setViewMode('elo');
                setSearchTerm('');
              }}
              className={`flex-1 sm:flex-none px-3 py-2 md:px-6 md:py-3 rounded-full text-xs md:text-base font-bold border transition shadow-sm ${
                viewMode === 'elo'
                  ? 'bg-[#4c2f8e] text-white border-[#4c2f8e]'
                  : 'bg-white text-[#4c2f8e] border-purple-200 hover:border-[#4c2f8e]'
              }`}
            >
              {t('eloRanking')}
            </button>

            <button
              type="button"
              onClick={() => {
                setViewMode('champions');
                setSearchTerm('');
              }}
              className={`flex-1 sm:flex-none px-3 py-2 md:px-6 md:py-3 rounded-full text-xs md:text-base font-bold border transition shadow-sm ${
                viewMode === 'champions'
                  ? 'bg-[#4c2f8e] text-white border-[#4c2f8e]'
                  : 'bg-white text-[#4c2f8e] border-purple-200 hover:border-[#4c2f8e]'
              }`}
            >
              {t('champions')}
            </button>
          </div>

          <p className="text-gray-500 mt-1 md:mt-3 text-xs md:text-lg leading-snug">
            {viewMode === 'elo'
              ? t('eloHint')
              : t('championsHint')}
          </p>
        </div>

        {/* SEARCH ELO + CHAMPIONS */}
        <div className="bg-white rounded-xl md:rounded-3xl shadow-md md:shadow-xl border border-purple-100 p-2 md:p-5 mb-3 md:mb-8">
          <div className="flex flex-row gap-2 md:gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 text-gray-400 text-sm md:text-base">
                🔍
              </span>

              <input
                type="text"
                placeholder={
                  viewMode === 'elo'
                    ? t('search')
                    : t('searchChampion')
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-200 rounded-lg md:rounded-2xl pl-9 md:pl-14 pr-3 md:pr-5 py-2 md:py-4 text-sm md:text-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4c2f8e]"
              />
            </div>

            <button
              type="button"
              className="hidden md:block bg-[#4c2f8e] text-white px-8 py-4 rounded-2xl text-base font-bold shadow-md hover:bg-[#3d2575] transition"
            >
              {t('searchButton')}
            </button>
          </div>
        </div>

        {/* LOADING ELO */}
        {isInitialLoading && viewMode === 'elo' ? (
          <div className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-purple-100 p-2 md:p-4">
            {[...Array(8)].map((_, index) => (
              <div
                key={index}
                className="h-12 md:h-14 bg-gray-100 rounded-lg md:rounded-2xl mb-2 md:mb-3 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <>
            {/* MODE ELO */}
            {viewMode === 'elo' && (
              <>
                {/* MOBILE ELO */}
                <div className="md:hidden space-y-1.5">
                  {filteredPlayers.length > 0 ? (
                    filteredPlayers.map((player, index) => {
                      const realRank = getRealRank(player);

                      return (
                        <div
                          key={`${player.nom}-${player.prenom}-${index}`}
                          className="bg-white rounded-lg border border-purple-100 shadow-sm px-3 py-2"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="inline-flex items-center justify-center min-w-7 h-7 rounded-full bg-purple-50 text-[#4c2f8e] text-xs font-black">
                                #{realRank}
                              </span>

                              <div className="min-w-0">
                                <div className="text-sm font-black text-gray-900 truncate">
                                  {player.nom} {player.prenom}
                                </div>

                                <div className="text-[11px] text-gray-500">
                                  {t('rank')} {realRank}
                                </div>
                              </div>
                            </div>

                            <div className="text-right shrink-0">
                              <div className="text-lg font-black text-[#4c2f8e] leading-none">
                                {player.elo?.toFixed(0)}
                              </div>

                              <span
                                className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${getCategoryStyle(
                                  player.elo
                                )}`}
                              >
                                {getCategorieFromElo(player.elo)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center text-gray-500 text-sm py-4">
                      {t('noPlayer')}
                    </div>
                  )}
                </div>

                {/* DESKTOP ELO */}
                <div className="hidden md:block overflow-hidden bg-white border border-purple-100 shadow-2xl rounded-3xl">
                  <table className="w-full text-left">
                    <thead className="bg-[#4c2f8e] text-white">
                      <tr>
                        <th className="px-8 py-5">#</th>
                        <th className="px-8 py-5">{t('player')}</th>
                        <th className="px-8 py-5">Elo</th>
                        <th className="px-8 py-5">{t('category')}</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredPlayers.length > 0 ? (
                        filteredPlayers.map((player, index) => {
                          const realRank = getRealRank(player);

                          return (
                            <tr
                              key={`${player.nom}-${player.prenom}-${index}`}
                              className="border-b hover:bg-[#f7f4ff]"
                            >
                              <td className="px-8 py-5">{realRank}</td>

                              <td className="px-8 py-5">
                                {player.nom} {player.prenom}
                              </td>

                              <td className="px-8 py-5 font-bold text-[#4c2f8e]">
                                {player.elo?.toFixed(0)}
                              </td>

                              <td className="px-8 py-5">
                                {getCategorieFromElo(player.elo)}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td
                            colSpan="4"
                            className="px-8 py-8 text-center text-gray-500"
                          >
                            {t('noPlayer')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* MODE CHAMPIONS */}
            {viewMode === 'champions' && (
              <>
                {isChampionsLoading ? (
                  <div className="bg-white rounded-xl md:rounded-3xl shadow-xl border border-purple-100 p-2 md:p-4">
                    {[...Array(8)].map((_, index) => (
                      <div
                        key={index}
                        className="h-12 md:h-14 bg-gray-100 rounded-lg md:rounded-2xl mb-2 md:mb-3 animate-pulse"
                      />
                    ))}
                  </div>
                ) : (
                  <>
                    {/* MOBILE CHAMPIONS */}
                    <div className="md:hidden space-y-1.5">
                      {filteredChampions.length > 0 ? (
                        filteredChampions.map((player, index) => {
                          const realRank = getChampionRealRank(player);

                          return (
                            <div
                              key={`${player.nom}-${player.prenom}-${index}`}
                              className="bg-white rounded-lg border border-purple-100 shadow-sm px-3 py-2"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="inline-flex items-center justify-center min-w-7 h-7 rounded-full bg-purple-50 text-[#4c2f8e] text-xs font-black">
                                    #{realRank}
                                  </span>

                                  <div className="min-w-0">
                                    <div className="text-sm font-black text-gray-900 truncate">
                                      {player.nom} {player.prenom}
                                    </div>

                                    <div className="text-[11px] text-gray-500">
                                      {t('rank')} {realRank}
                                    </div>
                                  </div>
                                </div>

                                <div className="text-right shrink-0">
                                  <div className="text-lg font-black text-[#4c2f8e] leading-none">
                                    {player.victories}
                                  </div>

                                  <div className="text-[10px] text-gray-500 font-semibold">
                                    {player.victories > 1
                                      ? t('winsLower')
                                      : t('win')}{' '}
                                    🏆
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center text-gray-500 text-sm py-4">
                          {t('noChampion')}
                        </div>
                      )}
                    </div>

                    {/* DESKTOP CHAMPIONS */}
                    <div className="hidden md:block overflow-hidden bg-white border border-purple-100 shadow-2xl rounded-3xl">
                      <table className="w-full text-left">
                        <thead className="bg-[#4c2f8e] text-white">
                          <tr>
                            <th className="px-8 py-5">#</th>
                            <th className="px-8 py-5">{t('winningPlayer')}</th>
                            <th className="px-8 py-5">{t('wins')}</th>
                          </tr>
                        </thead>

                        <tbody>
                          {filteredChampions.length > 0 ? (
                            filteredChampions.map((player, index) => {
                              const realRank = getChampionRealRank(player);

                              return (
                                <tr
                                  key={`${player.nom}-${player.prenom}-${index}`}
                                  className="border-b hover:bg-[#f7f4ff]"
                                >
                                  <td className="px-8 py-5">{realRank}</td>

                                  <td className="px-8 py-5 font-bold">
                                    {player.nom} {player.prenom}
                                  </td>

                                  <td className="px-8 py-5 font-black text-[#4c2f8e]">
                                    {player.victories}{' '}
                                    {player.victories > 1
                                      ? t('winsLower')
                                      : t('win')}{' '}
                                    🏆
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan="3"
                                className="px-8 py-8 text-center text-gray-500"
                              >
                                {t('noChampion')}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* FOOTER */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-2 md:gap-3 mt-4 md:mt-6">
          <button
            onClick={handleDownloadClassementPDF}
            className="bg-white border border-purple-100 px-4 py-2 md:px-5 md:py-3 rounded-full text-sm md:text-base font-bold text-[#4c2f8e] hover:border-[#4c2f8e] transition"
          >
            {t('export')}
          </button>

          <p className="text-gray-600 text-xs md:text-base text-center">
            {viewMode === 'elo' ? (
              <>
                <span className="font-bold text-[#4c2f8e]">
                  {filteredPlayers.length}
                </span>{' '}
                /{' '}
                <span className="font-bold text-[#4c2f8e]">
                  {players.length}
                </span>{' '}
                {t('playersMax')}
                {isFullLoading && (
                  <span className="text-gray-400">
                    {' '}
                    · {t('loading')}
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="font-bold text-[#4c2f8e]">
                  {filteredChampions.length}
                </span>{' '}
                /{' '}
                <span className="font-bold text-[#4c2f8e]">
                  {champions.length}
                </span>{' '}
                {t('champions')}
              </>
            )}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ClassementMendyx;
